import React from 'react'
import { Chip, Tooltip, Box } from '@mui/material'
import {
  LocalHospital as HealthIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon
} from '@mui/icons-material'

export interface TrendIndicator {
  direction: 'improving' | 'declining' | 'stable'
  change: number
  previousScore?: number
}

interface HealthScoreBadgeProps {
  band: 'Good' | 'Watch' | 'Poor' | 'Critical' | 'Not Configured' | 'No Data'
  color: 'green' | 'orange' | 'red' | 'black' | 'grey'
  score?: number | null
  size?: 'small' | 'medium'
  showIcon?: boolean
  trendIndicator?: TrendIndicator
}

export const HealthScoreBadge: React.FC<HealthScoreBadgeProps> = ({
  band,
  color,
  score,
  size = 'small',
  showIcon = false,
  trendIndicator
}) => {
  // "Not Configured" means no health-score rules apply to this exception
  // type. Rendering a grey chip there reads as live data with a poor
  // score; an empty slot is more honest. Suppress at the component level
  // so every consumer (cards, dashboard, drill-downs) inherits the rule.
  if (band === 'Not Configured') {
    return null
  }

  const getBackgroundColor = () => {
    switch (color) {
      case 'green':
        return '#4caf50'
      case 'orange':
        return '#ff9800'
      case 'red':
        return '#f44336'
      case 'black':
        return '#212121'
      case 'grey':
      default:
        return '#9e9e9e'
    }
  }

  const getTrendIcon = () => {
    if (!trendIndicator) return null
    
    const iconProps = {
      sx: {
        fontSize: size === 'small' ? 12 : 14,
        color: trendIndicator.direction === 'improving' ? '#4caf50' :
               trendIndicator.direction === 'declining' ? '#f44336' : '#9e9e9e'
      }
    }
    
    switch (trendIndicator.direction) {
      case 'improving':
        return <TrendingDownIcon {...iconProps} /> // Lower score is better
      case 'declining':
        return <TrendingUpIcon {...iconProps} /> // Higher score is worse
      case 'stable':
      default:
        return <TrendingFlatIcon {...iconProps} />
    }
  }

  const getTooltipText = () => {
    if (band === 'No Data') {
      return 'No sync data available - franchise has not been synced yet'
    }
    
    let description = ''
    switch (band) {
      case 'Good':
        description = 'Excellent data health'
        break
      case 'Watch':
        description = 'Minor issues that need attention'
        break
      case 'Poor':
        description = 'Significant issues requiring action'
        break
      case 'Critical':
        description = 'Severe issues needing immediate attention'
        break
    }
    
    let tooltipText = ''
    if (score !== null && score !== undefined) {
      tooltipText = `${description} (Score: ${score.toFixed(1)})`
    } else {
      tooltipText = description
    }
    
    // Add trend information if available
    if (trendIndicator) {
      const changeText = trendIndicator.change >= 0 ? `+${trendIndicator.change.toFixed(1)}` : trendIndicator.change.toFixed(1)
      const trendText = trendIndicator.direction === 'improving' ? 'improving' :
                       trendIndicator.direction === 'declining' ? 'declining' : 'stable'
      tooltipText += `\nTrend: ${trendText} (${changeText})`
      
      if (trendIndicator.previousScore !== undefined) {
        tooltipText += `\nPrevious: ${trendIndicator.previousScore.toFixed(1)}`
      }
    }
    
    return tooltipText
  }

  const getChipLabel = () => {
    if (band === 'No Data') {
      return '–'  // En dash character for "no data"
    }
    return band
  }

  const chipContent = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {showIcon && <HealthIcon sx={{ fontSize: size === 'small' ? 16 : 20 }} />}
      {getChipLabel()}
      {getTrendIcon()}
    </Box>
  )

  return (
    <Tooltip title={getTooltipText()} arrow>
      <Chip
        label={chipContent}
        size={size}
        style={{
          backgroundColor: getBackgroundColor(),
          color: 'white',
          fontWeight: 'bold',
          minWidth: size === 'small' ? '60px' : '80px'
        }}
      />
    </Tooltip>
  )
}